import {
    Box,
    Button,
    Typography,
    TextField,
    Autocomplete,
} from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { LoadingButton } from '@mui/lab';
import { Form, FormikProvider } from 'formik';

import Loading from '../loading';
import { CustomerTypes, UserTypes } from '../../types';
import { Team } from '../../types/teams.types';
import { Tag } from '../../types/tags.types';
import { TagAutocompleteWithCreate } from '../shared/tag-autocomplete-with-create';
import { useUserFormik } from './use-user-formik';

const COLORS = {
    primary: '#206FA5',
    primaryHover: '#2B5A87',
    textMuted: '#6B7A90',
    textFaint: '#9AA4B5',
    borderInput: '#DDE2E8',
    red: '#d32f2f',
};

const fieldLabelSx = {
    fontSize: '12px',
    fontWeight: 600,
    color: COLORS.textMuted,
    textTransform: 'uppercase' as const,
    letterSpacing: '.5px',
    mb: '4px',
};

const inputSx = {
    '& .MuiOutlinedInput-root': {
        fontSize: '14px',
        borderRadius: '8px',
    },
};

const requiredStar = (
    <Typography
        component="span"
        sx={{
            fontSize: '11px',
            color: COLORS.red,
            fontWeight: 500,
            ml: '3px',
        }}
    >
        *
    </Typography>
);

interface Props {
    isEdit: boolean;
    selectedUser?: UserTypes.Model;
    allTeams: Team[];
    customerSettings: CustomerTypes.OnboardingForm;
    isCustomerSettingsLoading: boolean;
    submitLabel?: string;
    cancelLabel?: string;
    onCancel(): void;
    onSuccess(): void;
}

export const UserForm: React.FC<Props> = ({
    isEdit,
    selectedUser,
    allTeams,
    customerSettings,
    isCustomerSettingsLoading,
    submitLabel,
    cancelLabel = 'Cancel',
    onCancel,
    onSuccess,
}) => {
    const {
        formik,
        selectedTeam,
        setSelectedTeam,
        selectedTags,
        setSelectedTags,
        availableTags,
        isLoading,
        handlePhoneChange,
        isPhoneRequired,
        isEmployeeIdRequired,
        isEmailRequired,
    } = useUserFormik({
        selectedUser,
        allTeams,
        customerSettings,
        onSuccess,
    });
    const { handleSubmit, errors, touched, getFieldProps } = formik;

    const formContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
            formContainerRef.current?.scrollTo({ top: 40, behavior: 'smooth' });
        }
    }, [formik.submitCount, formik.errors]);

    if (isCustomerSettingsLoading) {
        return <Loading loading="Loading Customer Settings..." />;
    }

    return (
        <Box ref={formContainerRef}>
            <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                    {/* Row 1: Name */}
                    <Box sx={{ display: 'flex', gap: '16px', mb: '20px' }}>
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                                <Typography sx={fieldLabelSx}>First Name</Typography>
                                {requiredStar}
                            </Box>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="e.g., John"
                                sx={inputSx}
                                {...getFieldProps('firstName')}
                                error={Boolean(errors.firstName) && touched.firstName}
                                helperText={touched.firstName && errors.firstName}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                                <Typography sx={fieldLabelSx}>Last Name</Typography>
                                {requiredStar}
                            </Box>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="e.g., Smith"
                                sx={inputSx}
                                {...getFieldProps('lastName')}
                                error={Boolean(errors.lastName) && touched.lastName}
                                helperText={touched.lastName && errors.lastName}
                            />
                        </Box>
                    </Box>

                    {/* Row 2: Phone + Employee ID */}
                    <Box sx={{ display: 'flex', gap: '16px', mb: '20px' }}>
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                                <Typography sx={fieldLabelSx}>Phone Number</Typography>
                                {isPhoneRequired && requiredStar}
                            </Box>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="+1 (555) 123-4567"
                                sx={inputSx}
                                value={formik.values.phoneNumber}
                                onChange={(e) => handlePhoneChange(e.target.value)}
                                error={Boolean(errors.phoneNumber) && touched.phoneNumber}
                                helperText={touched.phoneNumber && errors.phoneNumber}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                                <Typography sx={fieldLabelSx}>Employee ID</Typography>
                                {isEmployeeIdRequired && requiredStar}
                            </Box>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="e.g., EMP-1234"
                                sx={inputSx}
                                {...getFieldProps('employeeId')}
                                error={Boolean(errors.employeeId) && touched.employeeId}
                                helperText={touched.employeeId && errors.employeeId}
                            />
                        </Box>
                    </Box>

                    {/* Row 3: Email + Team */}
                    <Box sx={{ display: 'flex', gap: '16px', mb: '20px' }}>
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                                <Typography sx={fieldLabelSx}>Email</Typography>
                                {isEmailRequired && requiredStar}
                            </Box>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="e.g., john@company.com"
                                sx={inputSx}
                                {...getFieldProps('backupEmail')}
                                error={Boolean(errors.backupEmail) && touched.backupEmail}
                                helperText={touched.backupEmail && errors.backupEmail}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={fieldLabelSx}>Team</Typography>
                            <Autocomplete
                                size="small"
                                options={allTeams || []}
                                value={selectedTeam}
                                getOptionLabel={(option) => (option as any)?.name || ''}
                                isOptionEqualToValue={(option, value) =>
                                    (option as any)?.id === (value as any)?.id
                                }
                                onChange={(_, value) => setSelectedTeam(value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select a team..."
                                        sx={inputSx}
                                    />
                                )}
                                sx={{ '& .MuiAutocomplete-inputRoot': { borderRadius: '8px' } }}
                            />
                        </Box>
                    </Box>

                    {/* Row 4: Tags */}
                    <Box>
                        <Typography sx={fieldLabelSx}>Tags</Typography>
                        <TagAutocompleteWithCreate
                            availableTags={(availableTags as Tag[]) || []}
                            selectedTags={selectedTags}
                            onChange={setSelectedTags}
                            inputSx={inputSx}
                            primaryColor={COLORS.primary}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 1,
                            mt: 3,
                        }}
                    >
                        <Button
                            type="button"
                            onClick={onCancel}
                            variant="outlined"
                            sx={{
                                color: COLORS.textMuted,
                                borderColor: COLORS.borderInput,
                                textTransform: 'none',
                                fontSize: '14px',
                                borderRadius: '8px',
                            }}
                        >
                            {cancelLabel}
                        </Button>
                        <LoadingButton
                            type="submit"
                            loading={isLoading}
                            variant="contained"
                            sx={{
                                backgroundColor: COLORS.primary,
                                textTransform: 'none',
                                fontSize: '14px',
                                borderRadius: '8px',
                                '&:hover': { backgroundColor: COLORS.primaryHover },
                            }}
                        >
                            {submitLabel ?? (isEdit ? 'Update User' : 'Add User')}
                        </LoadingButton>
                    </Box>
                </Form>
            </FormikProvider>
        </Box>
    );
};
